import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { IPublicClientApplication } from "@azure/msal-browser";
import App from "./App";
import { AuthProvider } from "./auth/AuthContext";
import { loadAuthConfig } from "./auth/authConfig";
import { setRelease150 } from "./lib/release150";
import { applyLargeText } from "./lib/textSize";
import { installLinkPreload, preloadRoute } from "./lib/preload";
import { installDataPrefetch } from "./lib/dataPrefetch";
import { PersistQueryGate } from "./lib/PersistQueryGate";
import { installConditionalGet } from "./api/conditionalGet";
import { apiClient } from "./api/client";
import { createMsalInstance } from "./auth/msal";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { PageSkeleton } from "./components/Skeleton";
import { StepUpProvider } from "./components/auth/StepUpProvider";
import "@fontsource-variable/inter";
import "./index.css";

// Provider stack: React Query -> Router -> Auth -> step-up prompt -> ErrorBoundary, the same order Summit Engineering uses.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      gcTime: 5 * 60_000
    }
  }
});

/** Early shell skeleton painted immediately while /auth/config and MSAL finish (W0.3, O-R144; design tokens only). */
export function EarlyShellSkeleton() {
  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-900" data-testid="early-shell-skeleton">
      <aside className="hidden w-56 flex-none flex-col bg-brand-900 px-4 py-4 text-brand-50 md:flex" aria-hidden="true">
        <div className="text-sm font-semibold tracking-tight text-white">Summit Operations</div>
        <div className="text-[11px] text-brand-100/80">Summit Aviation, Inc.</div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-12 items-center border-b border-slate-200/80 bg-white/90 px-4" aria-hidden="true">
          <div className="h-4 w-40 animate-pulse rounded-md bg-slate-200/70 motion-reduce:animate-none" />
        </header>
        <main className="min-w-0 flex-1 px-3 pb-16 pt-5 sm:px-6">
          <PageSkeleton />
        </main>
      </div>
    </div>
  );
}

/**
 * Sign-in is decided at run time (GET /api/v1/auth/config): one image serves every environment. Microsoft Entra ID in
 * every deployed environment; the persona sign-in only when the API says it runs the Development stub.
 */
async function start(): Promise<void> {
  const rootEl = document.getElementById("root")!;
  const root = createRoot(rootEl);
  // Release 1.9.0 (W0.3): paint the shell skeleton before awaiting /auth/config and MSAL initialization.
  root.render(<EarlyShellSkeleton />);

  // Release 1.5.0 (perf): the landing page's chunk downloads while sign-in is decided (no waterfall), and hovering or
  // focusing any link in the app preloads its page (pure network, nothing visible changes).
  preloadRoute(window.location.pathname, { exact: true });
  installLinkPreload();
  const authConfig = await loadAuthConfig();
  // Release 1.5.0 switch from the API (/auth/config); off = behave exactly as 1.4.0.
  setRelease150(authConfig.release150 === true);
  if (authConfig.release150 === true) {
    // Release 1.5.0 (perf): answers stay in memory for 30 minutes so going back never starts from a blank screen;
    // conditional requests (ETag/304) and data prefetch on link hover. Off: exactly 1.4.0.
    queryClient.setDefaultOptions({ queries: { ...queryClient.getDefaultOptions().queries, gcTime: 30 * 60_000 } });
    installConditionalGet(apiClient);
    installDataPrefetch(queryClient);
    // Release 1.5.0 polish: "Larger text" from the account menu, kept on this device.
    applyLargeText();
  }
  let msal: IPublicClientApplication | null = null;
  if (authConfig.mode === "entra" && authConfig.configured) {
    try {
      msal = await createMsalInstance(authConfig);
    } catch (e) {
      console.error("Microsoft sign-in could not start", e);
    }
  }

  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        {/* React Router 7 (security review 2026-10-23): startTransition and relative splat paths are now the default. */}
        <BrowserRouter basename={import.meta.env.VITE_BASE_PATH ?? "/"}>
          <AuthProvider config={authConfig} msal={msal}>
            <PersistQueryGate>
              <StepUpProvider>
                <ErrorBoundary>
                  <App />
                </ErrorBoundary>
              </StepUpProvider>
            </PersistQueryGate>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>
  );
}

void start();